import {
	PAGES,
	WORDS,
	CANONICAL_HOST,
	CANONICAL_PROTOCOL,
} from "@/common/consts";
import { isScrabbleDictionaryPage } from "@/common/functions/dictionaryUtils";
import { alphabetizeTiles, sanitizeTiles } from "@/common/functions/tileUtils";
import { isEmpty } from "@/common/functions/objectUtils";

/**
 * Create a canonical URL from Vue router's current route
 * @param {String} route - The Vue Router's current route object
 * @return {String} The canonical URL with fixed protocol and no trailing slash
 */
export const canonical = (route) => {
	let isEnyePath = false;

	// Lowercase route (except in enye-related paths)
	const map = Array.prototype.map;
	let path = map
		.call(route.path, (letter) => {
			if (isEnyePath && letter === "N") return letter;
			return letter.toLowerCase();
		})
		.join("");

	// Fix missing leading slash in relative path
	path = path.charAt(0) !== "/" ? `/${path}` : path;

	// Sanitize and alphabetize tiles for results
	path = /\/([^\/]+)\/([^\/]+)\/?$/.test(path)
		? path.replace(/\/([^\/]+)\/([^\/]+)\/?$/, (match, p1, p2) => {
				if (["unscramble"].includes(p1)) {
					return (
						`/${p1}/` +
						alphabetizeTiles(
							sanitizeTiles(p2, route.name),
							route.name
						)
					);
				} else if (p1 === "scrabble-dictionary") {
					return (
						`/${p1}/` +
						sanitizeTiles(p2, PAGES.SCRABBLE_DICT_RESULTS)
					);
				} else {
					return `/${p1}/${p2}`;
				}
		  })
		: path;

	// Sanitize and alphabetize tiles for /4pics1word results
	path = /\/4pics1word\/\d{1,2}\/(.*)/.test(path)
		? path.replace(
				/\/4pics1word\/(\d{1,2})\/(.*)/,
				(match, p1, p2) =>
					`/4pics1word/${p1}/${alphabetizeTiles(
						sanitizeTiles(p2, PAGES.FOURPICS_RESULTS),
						PAGES.FOURPICS_RESULTS
					)}`
		  )
		: path;

	let url = `${CANONICAL_PROTOCOL}//${CANONICAL_HOST}${path}`;
	url = url.replace(/\/$/g, "");
	if (!isUnscramblerResultsPage(route.name)) {
		url = `${url}/`;
	}
	return {
		rel: "canonical",
		href: url,
	};
};

/**
 * Return true if the given route is some kind of unscrambler results page
 * @param {String} routeName - A $route.name string
 * @return {Boolean} true if route is an unscrambler results page;
 * false otherwise.
 */
export const isUnscramblerResultsPage = (routeName) => {
	return (
		routeName === PAGES.FOURPICS_RESULTS ||
		routeName === PAGES.SCRABBLE_DICT_RESULTS ||
		routeName === PAGES.SCRABBLE_EXACTMATCH_RESULTS ||
		routeName === PAGES.UNSCRAMBLER_RESULTS ||
		routeName === PAGES.UNSCRAMBLER_RESULTS_REMEMBER
	);
};

/**
 * Return the base URL for the corresponding routeName
 * @param {String} routeName - A $route.name string
 * @return {String} the pase relatie URL for the results page
 */
export const resultsPageURL = (routeName) => {
	if (is4Pics1Word(routeName)) return "/4pics1word";
	if (isScrabbleDictionaryPage(routeName)) return "/scrabble-dictionary";
	return "/unscramble";
};

/**
 * Return true if the given route is or is under 4 Pics 1 Word section
 * @param {String} routeName - A $route.name string
 * @return {Boolean} true if route is a 4pics1word page; false otherwise.
 */
export const is4Pics1Word = (routeName) => {
	return routeName === PAGES.FOURPICS || routeName === PAGES.FOURPICS_RESULTS;
};

/**
 * Given a page from words lists, it returns the type of word lists page it is.
 * @param {String} routeName - A $route.name string
 * @return {String} the type of words list page corresponding to the routeName
 */
export const getWordListMode = (routeName) => {
	switch (routeName) {
		case PAGES.WORDS_HAVING_INDEX:
		case PAGES.WORDS_HAVING_RESULTS:
			return WORDS.HAVING;
			break;
		case PAGES.WORDS_ENDING_INDEX:
		case PAGES.WORDS_ENDING_RESULTS:
		case PAGES.WORDS_ENDING_ING:
			return WORDS.ENDING;
			break;
		case PAGES.WORDS_STARTING_INDEX:
		case PAGES.WORDS_STARTING_RESULTS:
			return WORDS.STARTING;
			break;
		case PAGES.WORDS_BY_LENGTH_INDEX:
		case PAGES.WORDS_BY_LENGTH_RESULTS:
			return WORDS.LENGTH;
			break;
		case PAGES.WORDS_CONSONANTS:
		case PAGES.WORDS_ENDING_ING:
		case PAGES.WORDS_WITH_Q_NO_U:
		case PAGES.WORDS_VOWELS:
			return WORDS.GENERAL;
			break;

		default:
			return false;
	}
};

export const isWordsByLengthResultsPage = (route) => {
	return route.name == PAGES.WORDS_BY_LENGTH_RESULTS;
};

export const isWordsWithTheLetterResultsPage = (route) => {
	return route.name == PAGES.WORDS_HAVING_RESULTS;
};

export const isWordsThatStartsWithResultsPage = (route) => {
	return route.name == PAGES.WORDS_STARTING_RESULTS;
};

export const isWordsThatEndsInResultsPage = (route) => {
	return route.name == PAGES.WORDS_ENDING_RESULTS;
};

export const isConsonantsResultsPage = (route) => {
	return route.name == PAGES.WORDS_CONSONANTS;
};

export const isWordsEndingInINGResultsPage = (route) => {
	return route.name == PAGES.WORDS_ENDING_ING;
};

export const isQNoUResultsPage = (route) => {
	return route.name == PAGES.WORDS_WITH_Q_NO_U;
};

export const isVowelsResultsPage = (route) => {
	return route.name == PAGES.WORDS_VOWELS;
};

export const resetStoreForWordListQuery = (route, store) => {
	let urlParameter = "";

	if (getWordListMode(route.name)) {
		store.commit("clearAdvancedSearch");
		store.commit("clearAllTiles");
	}

	if (isWordsWithTheLetterResultsPage(route)) {
		urlParameter = route.params.letter;
		if (urlParameter.length > 1 && !urlParameter.includes("-and-")) {
			store.commit(
				"updateStartsWith",
				getLettersQueryParam(urlParameter, WORDS.STARTING)
			);
			store.commit(
				"updateEndsWith",
				getLettersQueryParam(urlParameter, WORDS.ENDING)
			);
			urlParameter = getLettersQueryParam(urlParameter, "with");
		}
		urlParameter = urlParameter.replace("-and-", "");
		store.commit("updateContains", urlParameter);
	}

	if (isWordsThatStartsWithResultsPage(route)) {
		urlParameter = route.params.letter;
		store.commit("updateStartsWith", urlParameter);
	}

	if (isWordsThatEndsInResultsPage(route)) {
		urlParameter = route.params.letter;
		store.commit("updateEndsWith", urlParameter);
	}

	if (isWordsEndingInINGResultsPage(route)) {
		store.commit("updateEndsWith", "ing");
	}

	if (isWordsByLengthResultsPage(route)) {
		urlParameter = route.params.length;
		const isHasLetters = /[a-zA-Z_]+$/.test(urlParameter);
		if (isHasLetters) {
			store.commit(
				"updateStartsWith",
				getLettersQueryParam(urlParameter, WORDS.STARTING)
			);
			store.commit(
				"updateEndsWith",
				getLettersQueryParam(urlParameter, WORDS.ENDING)
			);
			store.commit(
				"updateContains",
				getLettersQueryParam(urlParameter, "with")
			);
		}
		store.commit("updateWordlength", urlParameter.replace(/\D+/g, ""));
	}
};

export const getLettersQueryParam = (letters, prefixString) => {
	const string = letters.split(`${prefixString}-`)[1] || "";
	return string.indexOf("-") !== -1
		? string.substring(0, string.indexOf("-"))
		: string;
};

export const hasEndingSlashInPath = (path) => {
	return !isEmpty(path) && path.substr(-1) === "/";
};

/**
 * Takes a route name string and returns the page template
 * string value. Used for content grouping in Google Analytics.
 * @param {string} routeName The name of the route
 * @returns {string} The page template string value.
 */
export const getPageTemplateValue = (routeName) => {
	switch (routeName) {
		case PAGES.HOME:
			return "home";
		case PAGES.APP_ABOUT:
			return "about-app";
		case PAGES.APP_PRIVACY_POLICY:
			return "legal-info";
		case PAGES.FOURPICS:
			return "fourpics";
		case PAGES.FOURPICS_RESULTS:
		case PAGES.SCRABBLE_DICT_RESULTS:
		case PAGES.UNSCRAMBLER_RESULTS:
		case PAGES.WORDS_BY_LENGTH_RESULTS:
		case PAGES.WORDS_STARTING_RESULTS:
		case PAGES.WORDS_ENDING_RESULTS:
		case PAGES.WORDS_HAVING_RESULTS:
		case PAGES.WORDS_VOWELS:
		case PAGES.WORDS_CONSONANTS:
		case PAGES.WORDS_WITH_Q_NO_U:
		case PAGES.WORDS_ENDING_ING:
			return "results-en-new";
		case PAGES.WORDS_LISTS_INDEX:
			return "word-lists-index";
		case PAGES.WORDS_BY_LENGTH_INDEX:
		case PAGES.WORDS_STARTING_INDEX:
		case PAGES.WORDS_ENDING_INDEX:
		case PAGES.WORDS_HAVING_INDEX:
			return "words-lists";
		case PAGES.GAME_CHEATS:
			return "game-cheats";
		case PAGES.ANAGRAM:
		case PAGES.WORD_DESCRAMBLER:
		case PAGES.WORDBRAIN_ANSWERS:
		case PAGES.TEXT_TWIST:
		case PAGES.SCRABBLE_CHECKER:
		case PAGES.JUMBLE_SOLVER:
		case PAGES.SCRABBLE_DICT:
		case PAGES.WORD_CHUMS_CHEAT:
		case PAGES.UNSCRAMBLE:
		case PAGES.WORDCOOKIES:
		case PAGES.WORDFEUD:
		case PAGES.WORD_MAKER:
		case PAGES.WORD_UNSCRAMBLER:
		case PAGES.WWF:
		case PAGES.LETTERS_AND_BLANK:
		case PAGES.WORD_SWIPE_CHEATS:
		case PAGES.WORDSCAPES:
		case PAGES.WORD_CONNECT:
		case PAGES.CODY_CROSS:
		case PAGES.WORD_STORY:
			return "static-page-template";
		case PAGES.ARTICLE_INDEX:
			return "articles-index";
		case PAGES.ARTICLE_DETAIL:
			return "articles-detail";
		case PAGES.SAVED_WORDS:
			return "saved-words";
		case PAGES.NOT_FOUND:
			return "page-not-found";
		default:
			return "uncategorised";
	}
};
